/**
 * An item's history (migration 0088; the inventory plan's §15.6, step I2b).
 * Every posted movement of one item in a period, in date order: what it was,
 * which pool, in or out, how many and at what cost, the account on the other
 * side, and the pool's quantity and value after it. Each pool's figures the
 * day before the period opens the page, so a row's running figures can be
 * followed from a number the page shows.
 *
 * What each movement WAS is read from the entry, never stored: a count names
 * its line; a transfer's two lines name each other; a return, a sale's cost
 * and a vendor's credit are each an entry of their own kind.
 */

import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api, type ItemHistory, type MovementKind } from '../api.js';
import { localToday } from '../components/GoodsReturnForm.js';
import { formatCents } from '../lib/money.js';
import { showQuantity } from './ItemsPage.js';

export const MOVEMENT_LABELS: Record<MovementKind, string> = {
  received: 'Received',
  out: 'Out',
  sold: 'Sold',
  count: 'Count',
  transfer: 'Moved',
  returned: 'Returned By Customer',
  returned_to_vendor: 'Sent Back To Vendor',
  reversal: 'Reversal',
};

const poolLabel = (pool: { accountCode: string; fundName: string | null; projectName: string | null }) =>
  [pool.accountCode, pool.fundName, pool.projectName].filter((part) => part !== null).join(' · ');

export function ItemHistoryPage() {
  const { id = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = localToday();
  const from = searchParams.get('from') ?? `${today.slice(0, 4)}-01-01`;
  const to = searchParams.get('to') ?? today;
  const [history, setHistory] = useState<ItemHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    setError(null);
    api
      .itemHistory(id, from, to)
      .then((next) => current && setHistory(next))
      .catch((caught: unknown) => current && setError(caught instanceof ApiError ? caught.message : 'Could not read the item’s history.'));
    return () => {
      current = false;
    };
  }, [id, from, to]);

  function setPeriod(next: { from?: string; to?: string }) {
    const params = new URLSearchParams(searchParams);
    params.set('from', next.from ?? from);
    params.set('to', next.to ?? to);
    setSearchParams(params, { replace: true });
  }

  return (
    <section className="item-history">
      <h1>{history?.item ?? 'Item History'}</h1>
      <p className="muted">
        <Link to="/items">Items</Link> · Every movement in the period, and what each pool held after it.
      </p>
      <div className="row">
        <label>
          From{' '}
          <input type="date" value={from} onChange={(event) => setPeriod({ from: event.target.value })} />
        </label>
        <label>
          To{' '}
          <input type="date" value={to} onChange={(event) => setPeriod({ to: event.target.value })} />
        </label>
      </div>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {history && (
        <>
          <h2>Before {history.from}</h2>
          {history.opening.length === 0 ? (
            <p className="muted">Nothing on hand.</p>
          ) : (
            <div className="table-scroll">
              <table className="lines" aria-label="Before The Period">
                <thead>
                  <tr>
                    <th>Pool</th>
                    <th className="num">On Hand</th>
                    <th className="num">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {history.opening.map((pool) => (
                    <tr key={poolLabel(pool)}>
                      <td>{poolLabel(pool)}</td>
                      <td className="num">
                        {showQuantity(pool.onHand)} {history.unit}
                      </td>
                      <td className="num">{formatCents(pool.valueCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <h2>Movements</h2>
          {history.rows.length === 0 ? (
            <p className="muted">
              No movement from {history.from} to {history.to}.
            </p>
          ) : (
            <div className="table-scroll">
              <table className="lines" aria-label="Movements">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>What</th>
                    <th>Pool</th>
                    <th>Other Side</th>
                    <th className="num">In</th>
                    <th className="num">Out</th>
                    <th className="num">Cost</th>
                    <th className="num">On Hand</th>
                    <th className="num">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {history.rows.map((row) => (
                    <tr key={row.lineId}>
                      <td>
                        <Link to={`/journals/${row.headerId}`}>{row.date}</Link>
                      </td>
                      <td>{MOVEMENT_LABELS[row.kind]}</td>
                      <td>{poolLabel(row)}</td>
                      <td>{row.otherAccount ?? ''}</td>
                      <td className="num">{row.direction === 'in' ? showQuantity(row.quantity) : ''}</td>
                      <td className="num">{row.direction === 'out' ? showQuantity(row.quantity) : ''}</td>
                      <td className="num">{formatCents(row.cents)}</td>
                      <td className="num">
                        {showQuantity(row.onHand)} {history.unit}
                      </td>
                      <td className="num">{formatCents(row.valueCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
